import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { DollarSign, Plane, Loader } from 'lucide-react';
import client from '../../api/client';
import StatCard from './StatCard';
import DashboardTabBar from './DashboardTabBar';
import { useAuth, userIsAdmin, userIsSocio, userIsAccountant } from '../../context/AuthContext';
import AdminPtoStats from '../admin/AdminPtoStats';
import AdminCalendarPage from '../../vacation/pages/AdminCalendarPage';
import ErrorBoundary from './ErrorBoundary';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, fieldStyle } from '../../styles/tokens';

import { etiquetaProyecto } from '../../utils/formatProject';
/**
 * V65 — Pestaña Vacaciones del DASHBOARD GENERAL.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 LA REGLA QUE GOBIERNA ESTA PANTALLA: EL TIPO SOLO DONDE APARECEN LOS SALDOS
 * ═══════════════════════════════════════════════════════════════════════════
 * El dashboard de vacaciones de la rama de Adrian (referencia de QUÉ mostrar) vivía en
 * un portal de ADMIN, así que podía partir todo por tipo: `by_type`, top-5 Vacaciones,
 * top-5 Enfermedad, badges "Adelanto Vac / Adelanto Enf". **Esta pestaña la ve una
 * cuadrilla entera**, y "Enfermedad: 3" sobre un equipo de ocho es dato de salud.
 *
 * Es la misma decisión que V62 tomó en `getCalendar` ("NO AGREGUES `r.type` ACÁ. Si una
 * pantalla lo necesita, el que falta es un gate, no una columna"), llevada hasta acá:
 *   · Conteos y gráficos de la capa general → SIN tipo.
 *   · El tipo aparece únicamente en el bloque de COSTO, cuya audiencia
 *     (socio + contable + admin) es la misma que ya ve los saldos.
 * El backend colabora: `/api/vacation/dashboard` no emite `type` en ninguna forma.
 *
 * ⚠ DOS ALCANCES EN LA MISMA PANTALLA, a propósito. Los conteos vienen SCOPEADOS a lo
 * que el usuario alcanza (patrón V39); "Fuera próximamente" es GLOBAL. Son dos preguntas
 * distintas: la primera es "cómo va mi flujo", la segunda es "a quién no tengo mañana", y
 * scopear la segunda la rompe porque la gente se presta entre proyectos.
 *
 * ⚠ FORMA = GASTOS Y NÓMINA, no la rama de Adrian: fila de StatCards + gráficos de barra
 * + filtro de proyecto. Sin PieChart y sin cross-filter por click, que esas dos pestañas
 * no tienen. Y SIN export: el de PTO ya existe en Sage Report (V64) y es más completo —
 * duplicarlo sería una cuarta superficie de las mismas cifras.
 */

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const VERDE = '#16a34a', ROJO = '#dc2626';

const money0 = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Math.round(Number(v));
  return (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US');
};
const hrs = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');

/** Igual que el tooltip de las otras dos pestañas. */
function Tip({ active, payload, label, sufijo = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
                  padding: '7px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
      <div style={{ fontWeight: 700, color: NAVY, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: '#475569' }}>{hrs(p.value)}{sufijo}</div>
      ))}
    </div>
  );
}

export default function VacacionesDashboardTab({ tab, setTab, hasNomina, hideTabs }) {
  const { user } = useAuth();
  // 🔴 EL MISMO PAR QUE PortalLayout usa para 'Saldos y Costo' y que `authorize` usa en
  // /balances-report. Ocultar y restringir son la MISMA decisión (lección de V57): si
  // este gate y el del endpoint divergen, o se muestra una tarjeta que devuelve 403 o se
  // esconde un dato que la ruta sirve igual.
  const veCosto = userIsSocio(user) || userIsAccountant(user) || userIsAdmin(user);
  // 🔴 V65 — LAS MÉTRICAS DE GESTIÓN SON UNA TERCERA CAPA, solo admin. Vienen de
  // `/api/vacation/stats` (requireRole('owner')): pendientes por nivel, tiempo promedio
  // de aprobación y el desglose POR TIPO. El tipo puede aparecer acá justamente porque la
  // capa es admin — es la misma regla que gobierna la tarjeta de costo.
  const veGestion = userIsAdmin(user);

  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [projectId, setProjectId] = useState('');
  const [proyectos, setProyectos] = useState([]);
  const [data, setData] = useState(null);
  const [misAprob, setMisAprob] = useState(0);
  const [costo, setCosto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    client.get('/api/projects').then(r => setProyectos((r.data || []).filter(p => p.active !== false)))
      .catch(() => setProyectos([]));
  }, []);

  useEffect(() => {
    let vigente = true;
    setCargando(true); setError(null);
    const params = { year, ...(projectId ? { project_id: projectId } : {}) };
    Promise.all([
      client.get('/api/vacation/dashboard', { params }),
      client.get('/api/vacation/my-approvals').catch(() => ({ data: [] })),
    ])
      .then(([d, m]) => {
        if (!vigente) return;
        setData(d.data);
        setMisAprob(Array.isArray(m.data) ? m.data.length : (m.data?.length ?? 0));
      })
      .catch(e => vigente && setError(e.response?.data?.error || 'No se pudo cargar el panorama.'))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [year, projectId]);

  // El costo va en su propia llamada y solo si corresponde: su permiso es distinto y su
  // dato NO depende del filtro de proyecto ni del período (es el saldo vigente).
  useEffect(() => {
    if (!veCosto) { setCosto(null); return; }
    let vigente = true;
    client.get('/api/vacation/balances-report', { params: { year } })
      .then(r => vigente && setCosto(r.data))
      .catch(() => vigente && setCosto(null));
    return () => { vigente = false; };
  }, [veCosto, year]);

  const c = data?.counts;
  const porMes = (data?.by_month || []).map(m => ({ mes: MESES[m.month - 1], horas: m.horas }));
  const porProyecto = (data?.by_project || []).slice(0, 8)
    .map(p => ({ nombre: p.code || p.name, horas: p.horas }));
  const t = costo?.totals;
  const pasivo = t ? [
    { tipo: 'Vacaciones', adeudado: t.vacation.positive, adelantado: Math.abs(t.vacation.negative) },
    { tipo: 'Enfermedad', adeudado: t.sick.positive,     adelantado: Math.abs(t.sick.negative) },
  ] : [];

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <DashboardTabBar tab={tab} setTab={setTab} hasNomina={hasNomina} hideTabs={hideTabs} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} style={{ ...fieldStyle, width: 190 }}>
            <option value="">Todos los proyectos</option>
            {proyectos.map(p => <option key={p.id} value={p.id}>{etiquetaProyecto(p.code, p.name)}</option>)}
          </select>
          {/* ⚠ Selector de AÑO y no rango de fechas, a diferencia de Gastos y Nómina: el
              PTO es anual por construcción (la acumulación, los topes y el saldo son del
              año), así que un rango libre invitaría a pedir "1-mar a 15-jun", que para un
              saldo no significa nada. */}
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...fieldStyle, width: 100 }}>
            {[hoy.getFullYear() + 1, hoy.getFullYear(), hoy.getFullYear() - 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div style={{ ...bubbleStyle, background: '#fef2f2', color: '#991b1b',
                      border: '1px solid #fecaca', marginBottom: 14 }}>{error}</div>
      )}

      {cargando && !data ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', padding: 24 }}>
          <Loader size={16} className="spin" /> Cargando…
        </div>
      ) : (
        <>
          {/* ── Fila de StatCards: flujo, SIN tipo ─────────────────────────── */}
          {/* ═══════════════════════════════════════════════════════════════
              🔴 LA FILA DE ESTADOS SUMA AL TOTAL. Los cuatro estados son
              EXCLUYENTES Y EXHAUSTIVOS: pendiente + aprobada + denegada +
              cancelada = Solicitudes, exactamente.

              Antes no cuadraba, y por tres razones distintas que se veían igual:
                · `cancelled` entraba en el total y NO tenía tarjeta → el total
                  siempre era mayor que la suma y no había forma de explicarlo.
                · "Espera Socio(a)" es un SUBCONJUNTO de Pendientes, no un estado
                  propio: como tarjeta hacía doble conteo. Hoy da 0 en todos los
                  roles, así que el error estaba latente y habría aparecido con la
                  primera solicitud pasada de nivel 1. Pasa a subtítulo.
                · "Mi Aprobación" sale de OTRO endpoint y otro universo (registros
                  de cadena, sin filtro de año ni de proyecto). No es sumable con
                  estas por construcción, así que se separa.

              Un total que no cuadra con su desglose hace desconfiar de toda la
              pantalla, incluidos los números que sí están bien.
              ═══════════════════════════════════════════════════════════════ */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <StatCard label="Solicitudes" value={c?.total ?? 0} />
            <StatCard
              label="Pendientes"
              value={c?.pending ?? 0}
              subtitle={c?.waiting_socio > 0 ? `${c.waiting_socio} espera(n) Socio(a)` : undefined}
            />
            <StatCard label="Aprobadas"  value={c?.approved ?? 0} />
            <StatCard label="Denegadas"  value={c?.denied ?? 0} />
            <StatCard label="Canceladas" value={c?.cancelled ?? 0} />
          </div>

          {/* Fuera de la fila de estados A PROPÓSITO: no pertenece a ese universo.
              Es un corte por ACCIÓN —lo que espera algo de vos— y se cruza con
              varios de los estados de arriba. */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18,
                        paddingTop: 10, borderTop: `1px dashed ${BORDER_COLOR}` }}>
            <StatCard label="Mi Aprobación" value={misAprob} highlight={misAprob > 0}
                      subtitle="Esperan tu decisión" />
          </div>

          {/* ⚠ La contable no está en ninguna ganga, así que su fila de flujo sale toda
              en cero — y es correcto: ella no opera el flujo, opera el dinero. Se le dice,
              en vez de dejarla mirando seis ceros sin explicación. */}
          {data?.scope === 'scoped' && (c?.total ?? 0) === 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: -8, marginBottom: 16 }}>
              No hay solicitudes en tu alcance para {year}. Los conteos muestran solo los
              proyectos que conducís y tus propias solicitudes.
            </div>
          )}

          {/* ── La tarjeta grande: costo o quién está fuera ────────────────── */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
            {veCosto ? <TarjetaPasivo totals={t} /> : <TarjetaFuera lista={data?.out_soon || []} />}
            {veCosto && <TarjetaFuera lista={data?.out_soon || []} compacta />}
          </div>

          {/* ── Gráficos ──────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Panel titulo={`Horas fuera de trabajo por mes — ${year}`} nota="Aprobadas y pendientes, todos los tipos combinados." ancho={veCosto ? 380 : 560}>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={porMes} margin={{ top: 5, right: 10, left: -14, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip content={<Tip sufijo=" h" />} />
                  <Bar dataKey="horas" fill={AMBER} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel titulo="Horas fuera de trabajo por proyecto" nota="Aprobadas y pendientes, todos los tipos combinados." ancho={veCosto ? 380 : 560}>
              {porProyecto.length === 0 ? <Vacio /> : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={porProyecto} margin={{ top: 5, right: 10, left: -14, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="nombre" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip content={<Tip sufijo=" h" />} />
                    <Bar dataKey="horas" fill={NAVY} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            {/* 🔴 EL ÚNICO LUGAR CON TIPO, y su audiencia es la que ya ve saldos. */}
            {veCosto && t && (
              <Panel titulo="Pasivo: adeudado vs. adelantado" ancho={380}>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={pasivo} margin={{ top: 5, right: 10, left: -6, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="tipo" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v, n) => [money0(v), n === 'adeudado' ? 'Adeudado' : 'Adelantado']} />
                    <Bar dataKey="adeudado"   fill={VERDE} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="adelantado" fill={ROJO}  radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, lineHeight: 1.45 }}>
                  <strong>Adelantado</strong> es tiempo que el empleado debe: <strong>resta</strong> del pasivo.
                </div>
              </Panel>
            )}
          </div>

          {/* ── Capa 3: métricas de gestión — SOLO ADMIN ──────────────────
              🔴 V65 — Vienen de la pestaña Vacaciones de /admin, que se RETIRÓ. El
              componente se movió entero (con su arreglo de las 4 claves mal mapeadas);
              no se reimplementó. Mantener dos superficies de las mismas cifras fue lo
              que produjo el bug: AdminPtoStats quedó desalineado de V60 y nadie lo notó
              porque vivía aparte. */}
          {veGestion && (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
                Gestión de la cadena
              </h3>
              <ErrorBoundary seccion="Métricas de gestión">
                <AdminPtoStats />
              </ErrorBoundary>
            </div>
          )}

          {/* ── El calendario, adentro de la pestaña (V65) ────────────────
              ⚠ ES GLOBAL, y convive con conteos SCOPEADOS en la misma pantalla a
              propósito: la gente se presta entre proyectos, así que el que necesita
              saber que Fulano no viene puede no compartir ganga con él. Scopearlo lo
              rompe. Los dos alcances tienen que seguir conviviendo. */}
          <div style={{ marginTop: 18 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
              Calendario — toda la empresa
            </h3>
            <ErrorBoundary seccion="Calendario">
              <AdminCalendarPage />
            </ErrorBoundary>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * ⚠⚠ MUESTRA EL NETO **Y** EL ADELANTADO, no solo el total.
 * Medido en V64: el neto de enfermedad esconde $11.576 de tiempo adelantado. Un
 * descubierto grande baja el total y quien lo lee cree que el pasivo es menor del real.
 * Es la misma razón por la que los totales del export de V64 van en tres piezas.
 */
function TarjetaPasivo({ totals }) {
  if (!totals) return null;
  const adelantado = (totals.vacation.negative || 0) + (totals.sick.negative || 0);
  return (
    <div style={{ ...bubbleStyle, background: NAVY, border: `1px solid ${NAVY}`, flex: 1, minWidth: 300 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <DollarSign size={15} color="#fff" style={{ opacity: 0.85 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: '#cbd5e1',
                       textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Pasivo total estimado
        </span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.05 }}>
        {money0(totals.net_total)}
      </div>
      <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, color: '#e2e8f0' }}>
        <div>Vacaciones<div style={{ fontWeight: 700, fontSize: 13 }}>{money0(totals.vacation.net)}</div></div>
        <div>Enfermedad<div style={{ fontWeight: 700, fontSize: 13 }}>{money0(totals.sick.net)}</div></div>
      </div>
      {adelantado < 0 && (
        <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 9, lineHeight: 1.45 }}>
          Neto: incluye <strong>{money0(Math.abs(adelantado))}</strong> de tiempo adelantado que
          <strong> reduce</strong> el total. El adeudado bruto es {money0(totals.vacation.positive + totals.sick.positive)}.
        </div>
      )}
      <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 7 }}>
        Sobre {totals.with_rate} de {totals.with_rate + totals.without_rate} personas con tarifa registrada
        {totals.without_rate > 0 && ' — el resto no está incluido'}
      </div>
    </div>
  );
}

/**
 * La cara de la tarjeta para quien NO ve costo. **No es un placeholder**: es el dato que
 * esa audiencia sí necesita —a quién no tengo la semana que viene—, y por eso es GLOBAL y
 * no scopeado: la gente se presta entre proyectos.
 * ⚠ Sin tipo. Dice que la persona no está, no por qué.
 */
function TarjetaFuera({ lista, compacta }) {
  return (
    <div style={{ ...bubbleStyle, flex: 1, minWidth: compacta ? 260 : 300 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <Plane size={15} color={AMBER} />
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b',
                       textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Fuera en los próximos 7 días
        </span>
      </div>
      {lista.length === 0 ? (
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Nadie ausente en los próximos días.</div>
      ) : (
        <>
          <div style={{ fontSize: 26, fontWeight: 800, color: NAVY, lineHeight: 1.05, marginBottom: 8 }}>
            {lista.length}
          </div>
          <div style={{ maxHeight: 108, overflowY: 'auto' }}>
            {lista.slice(0, compacta ? 4 : 8).map(p => (
              <div key={p.id} style={{ fontSize: 11, padding: '3px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
                                       display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#334155' }}>{p.name}</span>
                <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {p.start_date.slice(5)} → {p.end_date.slice(5)}
                  {p.status === 'pending' && <span style={{ color: '#b45309' }}> · sin aprobar</span>}
                </span>
              </div>
            ))}
          </div>
          {lista.length > (compacta ? 4 : 8) && (
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5 }}>
              y {lista.length - (compacta ? 4 : 8)} más
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * ⚠ La `nota` NO es decoración: "Ausencias" a secas dejaba dos preguntas sin responder
 * —¿cuenta las pendientes? ¿es solo vacaciones, estando en la pestaña Vacaciones?— y la
 * segunda empujaba justo hacia pedir el desglose por tipo. Decir "todos los tipos
 * combinados" cierra las dos SIN emitir el tipo: que las ausencias tengan tipos no es
 * dato de nadie; cuál tuvo cada persona sí.
 */
function Panel({ titulo, nota, ancho, children }) {
  return (
    <div style={{ ...bubbleStyle, flex: 1, minWidth: ancho }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: '0 0 2px' }}>{titulo}</h3>
      {nota && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>{nota}</div>}
      {!nota && <div style={{ marginBottom: 8 }} />}
      {children}
    </div>
  );
}

const Vacio = () => (
  <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: '#94a3b8' }}>Sin datos en este alcance.</div>
);
